import React from 'react';
import { ClipboardList, Users, Calendar, HeartPulse, Check, ArrowRight, ShieldCheck, IdCard, Wallet } from 'lucide-react';

const ICONS = {
  intake: ClipboardList, matching: Users, booking: Calendar, active: HeartPulse,
  verify: ShieldCheck, profile: IdCard, matched: Users, paid: Wallet,
};

/**
 * Visual Care Journey Tracker — a 4-step roadmap (Intake → Matching → Booking →
 * Active Care) that shows the patient exactly where they are and what's next,
 * removing "what do I do now?" friction. Steps are clickable to jump to the action.
 *
 * Props:
 *   steps: [{ key, label, hint, done }]
 *   onStepClick?: (step) => void
 */
export const CareJourneyTracker = ({ steps = [], onStepClick, eyebrow = 'Your care journey', completeLabel = "You're in active care" }) => {
  const currentIndex = steps.findIndex((s) => !s.done);
  const completed = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((completed / steps.length) * 100) : 0;
  const next = currentIndex === -1 ? null : steps[currentIndex];

  return (
    <div className="cn-card p-5 sm:p-6 mb-6" data-testid="care-journey-tracker">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-cn-text-muted">{eyebrow}</p>
          <h3 className="font-display font-bold text-lg sm:text-xl text-cn-text dark:text-white leading-tight">
            {next ? `Next up: ${next.label}` : completeLabel}
          </h3>
          {next?.hint && <p className="text-xs text-cn-text-muted mt-0.5">{next.hint}</p>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-display font-bold text-cn-primary leading-none">{pct}%</div>
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-semibold mt-1">Complete</p>
        </div>
      </div>

      <ol className="flex items-start">
        {steps.map((s, i) => {
          const Icon = ICONS[s.key] || ClipboardList;
          const active = i === currentIndex;
          const done = s.done;
          const connectorFilled = done;
          return (
            <li key={s.key} className={`flex items-start ${i < steps.length - 1 ? 'flex-1' : 'flex-none'}`}>
              <button
                type="button"
                onClick={() => onStepClick?.(s)}
                data-testid={`journey-step-${s.key}`}
                className="flex flex-col items-center gap-1.5 shrink-0 group focus:outline-none"
                title={s.hint || s.label}
              >
                <span
                  className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all duration-300 group-hover:-translate-y-0.5
                    ${done
                      ? 'bg-cn-primary border-cn-primary text-white'
                      : active
                        ? 'border-cn-accent text-cn-accent bg-cn-accent/10 ring-4 ring-cn-accent/15 animate-pulse-soft'
                        : 'border-cn-border text-cn-text-muted bg-white dark:bg-cn-ink-800'}`}
                >
                  {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </span>
                <span
                  className={`text-[11px] sm:text-xs font-semibold text-center leading-tight max-w-[72px]
                    ${active ? 'text-cn-accent' : done ? 'text-cn-text dark:text-white' : 'text-cn-text-muted'}`}
                >
                  {s.label}
                </span>
              </button>
              {i < steps.length - 1 && (
                <span className={`flex-1 h-0.5 mt-[22px] mx-1 sm:mx-2 rounded-full transition-colors ${connectorFilled ? 'bg-cn-primary' : 'bg-cn-border'}`} />
              )}
            </li>
          );
        })}
      </ol>

      {next && (
        <button
          type="button"
          onClick={() => onStepClick?.(next)}
          data-testid="journey-next-cta"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-cn-primary hover:gap-2.5 transition-all"
        >
          {next.cta || `Continue to ${next.label}`} <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default CareJourneyTracker;
